import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { DateField } from "@/components/common";
import { toast } from "sonner";
import { FileUploadField } from "@/components/tasks/WorkflowCompleteDialog";

const SHIP_CHECKLIST = [  ["correct_customer", "Correct customer"], ["correct_invoice", "E-Invoice available"],
  ["correct_product", "Correct SKU"], ["correct_quantity", "Correct quantity"],
  ["correct_packing", "Correct packing"], ["correct_address", "Correct ship-to"],
  ["qc_approved", "QC approved"], ["documents_complete", "Documentation complete"],
  ["vehicle_recorded", "Vehicle checked in"], ["loading_completed", "Loading completed"],
];

async function postAction(path, body, okMsg) {
  try {
    await api.post(path, body || {});
    toast.success(okMsg);
    return true;
  } catch (e) {
    toast.error(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Action failed");
    return false;
  }
}

export function ChecklistDialog({ open, onOpenChange, shipment: s, onChanged }) {
  const [checks, setChecks] = useState(s.dispatch_checklist || {});
  useEffect(() => {
    if (open) setChecks(s.dispatch_checklist || {});
  }, [open, s.dispatch_checklist]);
  const save = async () => {
    if (await postAction(`/shipments/${s.shipment_id}/checklist`, { checks }, "Checklist saved")) {
      onOpenChange(false);
      onChanged?.();
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-card border-border" data-testid="shipment-checklist-dialog">
        <DialogHeader><DialogTitle className="font-heading text-xl uppercase">Final Dispatch Verification — {s.shipment_id}</DialogTitle><DialogDescription className="sr-only">Confirm all final dispatch checks</DialogDescription></DialogHeader>
        <div className="space-y-2">
          {SHIP_CHECKLIST.map(([k, label]) => (
            <label key={k} className="flex items-center gap-3 text-sm border border-border rounded-md px-3 py-2">
              <input type="checkbox" className="w-4 h-4 accent-primary" data-testid={`ship-check-${k}`} checked={!!checks[k]}
                onChange={(e) => setChecks({ ...checks, [k]: e.target.checked })} />
              {label}
            </label>
          ))}
          <Button className="w-full bg-primary text-primary-foreground" onClick={save} data-testid="ship-checklist-save">Save Verification</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function LrDialog({ open, onOpenChange, shipment: s, onChanged }) {
  const [lr, setLr] = useState({});
  const submit = async () => {
    if (await postAction(`/shipments/${s.shipment_id}/lr-upload`, lr, "LR uploaded — Sales communication task created")) {
      onOpenChange(false);
      onChanged?.();
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-card border-border" data-testid="lr-dialog">
        <DialogHeader><DialogTitle className="font-heading text-xl uppercase">LR / Dispatch Proof — {s.shipment_id}</DialogTitle><DialogDescription className="sr-only">Upload LR details and dispatch proof</DialogDescription></DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs">LR Number *</Label><Input value={lr.lr_number || ""} onChange={(e) => setLr({ ...lr, lr_number: e.target.value })} data-testid="lr-number" /></div>
            <DateField label="LR Date" value={lr.lr_date || ""} onChange={(v) => setLr({ ...lr, lr_date: v })} testId="lr-date" />
            <div><Label className="text-xs">Transporter</Label><Input value={lr.transporter || ""} onChange={(e) => setLr({ ...lr, transporter: e.target.value })} data-testid="lr-transporter" /></div>
            <div><Label className="text-xs">Packages</Label><Input type="number" value={lr.packages || ""} onChange={(e) => setLr({ ...lr, packages: parseInt(e.target.value) || null })} data-testid="lr-packages" /></div>
          </div>
          <FileUploadField kind="lr" label="LR copy / dispatch proof" dataTestId="lr-upload" onUploaded={(p) => setLr({ ...lr, document_path: p })} />
          <Button className="w-full bg-primary text-primary-foreground" disabled={!lr.lr_number || !lr.document_path} onClick={submit} data-testid="lr-submit">Upload LR</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function TransitDialog({ open, onOpenChange, shipment: s, onChanged }) {
  const [form, setForm] = useState({});
  const submit = async () => {
    if (await postAction(`/shipments/${s.shipment_id}/in-transit`, form, "Marked in transit")) {
      onOpenChange(false);
      onChanged?.();
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-card border-border" data-testid="transit-dialog">
        <DialogHeader><DialogTitle className="font-heading text-xl uppercase">Mark In Transit — {s.shipment_id}</DialogTitle><DialogDescription className="sr-only">Record shipment tracking and expected delivery</DialogDescription></DialogHeader>
        <div className="space-y-3">
          <div><Label className="text-xs">Tracking reference</Label><Input value={form.tracking_reference || ""} onChange={(e) => setForm({ ...form, tracking_reference: e.target.value })} data-testid="transit-ref" /></div>
          <DateField label="Expected delivery" value={form.expected_delivery || ""} onChange={(v) => setForm({ ...form, expected_delivery: v })} testId="transit-eta" />
          <Button className="w-full bg-primary text-primary-foreground" onClick={submit} data-testid="transit-submit">Confirm In Transit</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function DeliverDialog({ open, onOpenChange, shipment: s, onChanged }) {
  const [form, setForm] = useState({});
  const submit = async () => {
    if (await postAction(`/shipments/${s.shipment_id}/delivered`, form, "Delivered — POD task created for Sales")) {
      onOpenChange(false);
      onChanged?.();
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-card border-border" data-testid="deliver-dialog">
        <DialogHeader><DialogTitle className="font-heading text-xl uppercase">Mark Delivered — {s.shipment_id}</DialogTitle><DialogDescription className="sr-only">Record receiver and delivered quantity</DialogDescription></DialogHeader>
        <div className="space-y-3">
          <div><Label className="text-xs">Received by *</Label><Input value={form.receiver_name || ""} onChange={(e) => setForm({ ...form, receiver_name: e.target.value })} data-testid="deliver-receiver" /></div>
          <div><Label className="text-xs">Quantity received</Label><Input type="number" value={form.quantity_received || ""} onChange={(e) => setForm({ ...form, quantity_received: parseInt(e.target.value) || null })} data-testid="deliver-qty" /></div>
          <Button className="w-full bg-signal-green hover:bg-signal-green/90 text-white" onClick={submit} data-testid="deliver-submit">Confirm Delivery</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function CancelDialog({ open, onOpenChange, shipment: s, onChanged }) {
  const [cancelReason, setCancelReason] = useState("");
  const submit = async () => {
    if (await postAction(`/shipments/${s.shipment_id}/cancel`, { reason: cancelReason }, "Shipment cancelled — quantity released")) {
      setCancelReason("");
      onOpenChange(false);
      onChanged?.();
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-card border-border" data-testid="shipment-cancel-dialog">
        <DialogHeader><DialogTitle className="font-heading text-xl uppercase">Cancel Shipment — {s.shipment_id}</DialogTitle><DialogDescription className="sr-only">Cancel an undispatched shipment and release its allocated quantity</DialogDescription></DialogHeader>
        <div className="space-y-3">
          <p className="text-xs text-muted-foreground">This releases the allocated quantity back to the order. Any open loading task for this shipment will be cancelled.</p>
          <div><Label className="text-xs">Cancellation reason *</Label><Textarea value={cancelReason} onChange={(e) => setCancelReason(e.target.value)} data-testid="shipment-cancel-reason" /></div>
          <Button className="w-full bg-signal-red hover:bg-signal-red/90 text-white" disabled={cancelReason.trim().length < 3}
            onClick={submit}
            data-testid="shipment-cancel-submit">CONFIRM CANCELLATION</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
